import React, { useEffect, useState } from "react";
import api, { fileUrl, uploadImage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, Button, Modal, Field, Input, Empty } from "@/components/ui-kit";
import { Plus, Pencil, Trash2, Camera, ExternalLink, Handshake } from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", url: "", description: "", logo: "", order: 0 };

export default function Sponsors() {
  const { hasRole } = useAuth();
  const isAdmin = hasRole("admin");
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => { setLoading(true); api.get("/sponsors").then((r) => setSponsors(r.data)).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(load, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (s) => { setEditing(s); setForm({ ...empty, ...s }); setOpen(true); };

  const onLogo = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try { const logo = await uploadImage(file); setForm((f) => ({ ...f, logo })); toast.success("Logo ajouté"); }
    catch { toast.error("Échec de l'upload"); } finally { setUploading(false); }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, order: parseInt(form.order) || 0 };
      if (editing) await api.put(`/sponsors/${editing.id}`, payload);
      else await api.post("/sponsors", payload);
      toast.success(editing ? "Sponsor mis à jour" : "Sponsor ajouté");
      setOpen(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); } finally { setSaving(false); }
  };

  const remove = async (id) => { if (!window.confirm("Supprimer ce sponsor ?")) return; await api.delete(`/sponsors/${id}`); toast.success("Sponsor supprimé"); load(); };

  return (
    <div data-testid="sponsors-page">
      <PageHeader title="Nos partenaires" subtitle="Les sponsors et partenaires qui soutiennent le club."
        action={isAdmin && <Button data-testid="add-sponsor-btn" onClick={openNew}><Plus size={16} /> Ajouter un sponsor</Button>} />

      {loading ? <Empty text="Chargement..." /> : sponsors.length ? (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {sponsors.map((s, i) => (
            <div key={s.id} data-testid="sponsor-card"
              className="group relative bg-card border border-border rounded-2xl overflow-hidden flex flex-col animate-fade-up hover:border-primary/60 transition-colors"
              style={{ animationDelay: `${i * 60}ms` }}>
              <div className="relative aspect-[16/9] bg-white flex items-center justify-center p-6">
                {s.logo ? (
                  <img src={fileUrl(s.logo)} alt={s.name} className="max-h-full max-w-full object-contain" />
                ) : (
                  <div className="text-muted-foreground flex flex-col items-center gap-2"><Handshake size={40} strokeWidth={1.2} /><span className="text-sm font-bold text-foreground/70">{s.name}</span></div>
                )}
                {isAdmin && (
                  <div className="absolute top-2 right-2 flex gap-1">
                    <button data-testid={`edit-sponsor-btn-${s.id}`} onClick={() => openEdit(s)} className="p-2 rounded-full bg-black/60 text-white hover:bg-primary transition-colors"><Pencil size={14} /></button>
                    <button data-testid={`delete-sponsor-btn-${s.id}`} onClick={() => remove(s.id)} className="p-2 rounded-full bg-black/60 text-white hover:bg-destructive transition-colors"><Trash2 size={14} /></button>
                  </div>
                )}
              </div>
              <div className="p-5 flex flex-col flex-1">
                <h3 className="font-display text-lg font-bold uppercase tracking-tight leading-tight">{s.name}</h3>
                {s.description && <p className="text-sm text-muted-foreground mt-2 leading-relaxed flex-1">{s.description}</p>}
                {s.url && (
                  <a href={s.url} target="_blank" rel="noopener noreferrer" data-testid={`sponsor-link-${s.id}`}
                    className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold text-primary hover:underline">
                    <ExternalLink size={14} /> Visiter le site
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : <Empty text={isAdmin ? "Aucun sponsor. Cliquez sur « Ajouter un sponsor »." : "Aucun partenaire pour le moment."} />}

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Modifier le sponsor" : "Nouveau sponsor"}>
        <form onSubmit={save}>
          <Field label="Logo">
            <div className="flex items-center gap-4">
              {form.logo ? (
                <img src={fileUrl(form.logo)} alt="aperçu" className="w-24 h-16 rounded-lg object-contain bg-white border border-border p-1" />
              ) : (
                <div className="w-24 h-16 rounded-lg bg-secondary flex items-center justify-center text-muted-foreground"><Camera size={22} /></div>
              )}
              <label className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-secondary border border-border text-sm font-semibold cursor-pointer hover:border-primary transition-colors">
                <Camera size={15} /> {uploading ? "Envoi..." : (form.logo ? "Changer" : "Ajouter un logo")}
                <input data-testid="sponsor-logo-input" type="file" accept="image/*" className="hidden" onChange={onLogo} disabled={uploading} />
              </label>
            </div>
          </Field>
          <Field label="Nom"><Input data-testid="sponsor-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nom du partenaire" /></Field>
          <Field label="Lien du site (optionnel)"><Input data-testid="sponsor-url" type="url" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://..." /></Field>
          <Field label="Description (optionnel)">
            <textarea data-testid="sponsor-description" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="w-full rounded-lg bg-secondary border border-border p-3 text-sm outline-none focus:border-primary resize-y" placeholder="Quelques mots sur le partenaire..." />
          </Field>
          <Field label="Ordre d'affichage"><Input data-testid="sponsor-order" type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} /></Field>
          <Button data-testid="save-sponsor-btn" className="w-full mt-2" disabled={saving || uploading}>{saving ? "Enregistrement..." : "Enregistrer"}</Button>
        </form>
      </Modal>
    </div>
  );
}
